import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check, Search } from "lucide-react";
import { useJeMobil } from "../lib/mobil";

/**
 * Rozbalovací výběr (7B ze schváleného plátna) — náhrada za <select className="field">.
 * Píše se stejně jako select: value, onChange(e) s e.target.value, uvnitř <option>.
 *
 * Na počítači se otevře vlastní menu s fajfkou u vybrané možnosti; od 8 možností
 * nahoře hledání (bez ohledu na diakritiku). Na telefonu zůstává systémový výběr —
 * je pro prst pohodlnější a umí ho každý.
 *
 * Menu se kreslí do <body> s pevnou pozicí, aby ho neořízla karta s overflow-hidden.
 */
const bezDiakritiky = (t) => String(t).normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const textUzlu = (n) => {
  if (n == null || n === false || n === true) return "";
  if (typeof n === "string" || typeof n === "number") return String(n);
  if (Array.isArray(n)) return n.map(textUzlu).join("");
  if (n.props) return textUzlu(n.props.children);
  return "";
};

// Rozbalí <option> z dětí — i z polí (map), fragmentů a podmínek.
const moznostiZDeti = (deti) => {
  const out = [];
  const projdi = (n) => {
    React.Children.forEach(n, (c) => {
      if (!c) return;
      if (c.type === React.Fragment) return projdi(c.props.children);
      if (c.type === "option") {
        const label = textUzlu(c.props.children);
        const value = c.props.value !== undefined ? String(c.props.value) : label;
        out.push({ value, label, disabled: !!c.props.disabled });
      }
    });
  };
  projdi(deti);
  return out;
};

export default function Vyber({ value, onChange, className = "", disabled = false, children, id,
  "data-testid": testid, "aria-label": ariaLabel, ...zbytek }) {
  const mobil = useJeMobil();
  const moznosti = useMemo(() => moznostiZDeti(children), [children]);
  const [otevreno, setOtevreno] = useState(false);
  const [dotaz, setDotaz] = useState("");
  const [aktivni, setAktivni] = useState(-1);
  const [poloha, setPoloha] = useState(null);
  const tlacitko = useRef(null);
  const menu = useRef(null);
  const hledani = useRef(null);

  const hodnota = value == null ? "" : String(value);
  const vybrana = moznosti.find((m) => m.value === hodnota);
  const sHledanim = moznosti.length >= 8;
  const viditelne = useMemo(() => {
    const q = bezDiakritiky(dotaz.trim());
    return q ? moznosti.filter((m) => bezDiakritiky(m.label).includes(q)) : moznosti;
  }, [moznosti, dotaz]);

  const zmerit = () => {
    const r = tlacitko.current?.getBoundingClientRect();
    if (!r) return;
    const dole = window.innerHeight - r.bottom;
    const nahoru = dole < 260 && r.top > dole;
    setPoloha({ left: r.left, width: Math.max(r.width, 200), top: nahoru ? undefined : r.bottom + 6,
      bottom: nahoru ? window.innerHeight - r.top + 6 : undefined, max: Math.min(320, (nahoru ? r.top : dole) - 16) });
  };

  const otevri = () => {
    if (disabled) return;
    zmerit();
    setDotaz("");
    setAktivni(Math.max(0, moznosti.findIndex((m) => m.value === hodnota)));
    setOtevreno(true);
  };
  const zavri = (vratFokus = true) => {
    setOtevreno(false);
    if (vratFokus) tlacitko.current?.focus();
  };
  const vyber = (m) => {
    if (!m || m.disabled) return;
    if (m.value !== hodnota) onChange?.({ target: { value: m.value, name: zbytek.name } });
    zavri();
  };

  useLayoutEffect(() => { if (otevreno && sHledanim) hledani.current?.focus(); }, [otevreno, sHledanim]);

  // Klik mimo, posun stránky nebo změna velikosti okna menu zavře.
  useEffect(() => {
    if (!otevreno) return undefined;
    const klik = (e) => {
      if (menu.current?.contains(e.target) || tlacitko.current?.contains(e.target)) return;
      zavri(false);
    };
    const posun = (e) => { if (!menu.current?.contains(e.target)) zavri(false); };
    document.addEventListener("mousedown", klik);
    window.addEventListener("scroll", posun, true);
    window.addEventListener("resize", zmerit);
    return () => {
      document.removeEventListener("mousedown", klik);
      window.removeEventListener("scroll", posun, true);
      window.removeEventListener("resize", zmerit);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otevreno]);

  const klavesy = (e) => {
    if (!otevreno) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) { e.preventDefault(); otevri(); }
      return;
    }
    if (e.key === "Escape") { e.preventDefault(); zavri(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setAktivni((a) => Math.min(viditelne.length - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setAktivni((a) => Math.max(0, a - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); vyber(viditelne[aktivni]); }
    else if (e.key === "Tab") zavri(false);
  };

  if (mobil) {
    return (
      <select id={id} className={className} value={hodnota} disabled={disabled} data-testid={testid}
        aria-label={ariaLabel} onChange={(e) => onChange?.(e)} {...zbytek}>
        {children}
      </select>
    );
  }

  return (
    <>
      <button ref={tlacitko} type="button" id={id} disabled={disabled} data-testid={testid} aria-label={ariaLabel}
        aria-haspopup="listbox" aria-expanded={otevreno} onClick={() => (otevreno ? zavri() : otevri())} onKeyDown={klavesy}
        className={`${className} !flex items-center justify-between gap-2 text-left disabled:opacity-60 disabled:cursor-not-allowed`}
        style={otevreno ? { borderColor: "#ff6a13", boxShadow: "0 0 0 3px rgba(255,106,19,.15)" } : undefined}>
        <span className={`truncate ${vybrana ? "" : "text-muted"}`}>{vybrana ? vybrana.label : "Vyber…"}</span>
        <ChevronDown size={16} className="shrink-0 text-muted transition-transform duration-200"
          style={{ transform: otevreno ? "rotate(180deg)" : "none" }} />
      </button>
      {otevreno && poloha && createPortal(
        <div ref={menu} className="vyber-menu fixed z-[200] bg-white rounded-card border border-line-softer shadow-panel p-1.5 flex flex-col"
          style={{ left: poloha.left, width: poloha.width, top: poloha.top, bottom: poloha.bottom, maxHeight: poloha.max,
            transformOrigin: poloha.top !== undefined ? "top center" : "bottom center" }}
          onKeyDown={klavesy}>
          {sHledanim && (
            <label className="relative block mb-1 shrink-0">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-2" />
              <input ref={hledani} value={dotaz} onChange={(e) => { setDotaz(e.target.value); setAktivni(0); }}
                placeholder="Hledat" aria-label="Hledat v možnostech"
                className="w-full h-9 pl-8 pr-2.5 rounded-[8px] border border-line bg-inputbg text-[13px] outline-none focus:border-accent focus:ring-2 focus:ring-accent/15" />
            </label>
          )}
          <div role="listbox" className="overflow-y-auto min-h-0">
            {viditelne.map((m, i) => (
              <button key={m.value + i} type="button" role="option" aria-selected={m.value === hodnota} disabled={m.disabled}
                data-aktivni={i === aktivni} onMouseEnter={() => setAktivni(i)} onClick={() => vyber(m)}
                className={`vyber-polozka w-full min-h-[36px] px-2.5 py-1.5 rounded-[8px] flex items-center justify-between gap-2 text-left text-[13px] text-ink disabled:text-muted-2 ${
                  m.value === hodnota ? "font-600" : "font-500"}`}>
                <span className="min-w-0">{m.label}</span>
                {m.value === hodnota && <Check size={14} strokeWidth={3} className="shrink-0 text-accent" />}
              </button>
            ))}
            {!viditelne.length && <div className="px-2.5 py-2 text-[12.5px] text-muted">Nic nenalezeno</div>}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
